import BackButton from "../components/BackButton";
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { MessageCircle, Send, Users, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ReportBlockMenu from '../components/ReportBlockMenu';

const DICEBEAR_BASE = 'https://api.dicebear.com/7.x';

// Resolve a chat sender's avatar (uploaded photo → chosen DiceBear → an
// initials image) — mirrors RankingsTab.buildRankAvatar. The backend enriches
// each message with avatar_type/style/seed/url so a real avatar always shows.
function buildChatAvatar(msg) {
  if (msg?.avatar_type === 'photo' && msg?.avatar_url) {
    return msg.avatar_url.startsWith('http')
      ? msg.avatar_url
      : `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${msg.avatar_url}`;
  }
  if (msg?.avatar_type === 'dicebear' && msg?.avatar_style && msg?.avatar_seed) {
    return `${DICEBEAR_BASE}/${msg.avatar_style}/svg?seed=${encodeURIComponent(msg.avatar_seed)}&size=64&backgroundColor=transparent`;
  }
  return `${DICEBEAR_BASE}/initials/svg?seed=${encodeURIComponent(msg?.username || '?')}&size=64&backgroundColor=transparent`;
}

// Merge two message lists by id (dedupe) and keep ascending chronological
// order. ISO-8601 `created_at` strings sort correctly lexicographically.
// Used so the 10s poll + "Load older" both fold into a single list without
// dropping previously-loaded history.
function mergeAsc(prev, incoming) {
  if (!incoming || incoming.length === 0) return prev;
  const map = new Map();
  for (const m of prev) map.set(m.id, m);
  for (const m of incoming) map.set(m.id, m);
  return Array.from(map.values()).sort((a, b) => {
    const ax = a.created_at || '';
    const bx = b.created_at || '';
    return ax < bx ? -1 : ax > bx ? 1 : 0;
  });
}

export default function CommunityChat({ leagueId, leagueName, onBack }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Keyset "Load older" state — `hasMore` reflects whether older history
  // exists beyond what we hold; the cursor is derived from messages[0].
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [muted, setMuted] = useState(null);
  const scrollRef = useRef(null);
  const initializedRef = useRef(false);   // seed hasMore only on first load
  const isPrependRef = useRef(false);      // suppress auto-scroll on prepend
  const hasScrolledOnceRef = useRef(false);

  // The Radix ScrollArea forwards `ref` to its Root (overflow-hidden); the
  // actual scroller is the Viewport child. Grab it for scroll control.
  const getViewport = useCallback(
    () => scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]'),
    [],
  );

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get('/community/messages', { params: { league_id: leagueId || 'global', limit: 50 } });
      const incoming = res.data.messages || [];
      setMessages(prev => mergeAsc(prev, incoming));
      // Seed the older-history flag only on the FIRST non-empty load (so a
      // chat that opens empty then grows past 50 via polling still gets the
      // cursor seeded); later polls fetch the newest window and must not
      // reset the cursor state.
      if (!initializedRef.current && incoming.length > 0) {
        setHasMore(!!res.data.has_more);
        initializedRef.current = true;
      }
    } catch (e) {
      // If endpoint doesn't exist yet, show empty (only before first success)
      if (!initializedRef.current) setMessages([]);
    } finally { setLoading(false); }
  }, [leagueId]);

  // Reset everything when the room changes so we don't merge across leagues.
  useEffect(() => {
    initializedRef.current = false;
    hasScrolledOnceRef.current = false;
    setMessages([]);
    setHasMore(false);
    setLoading(true);
  }, [leagueId]);

  // "Load older messages" — keyset pagination via `before=<oldest created_at>`
  // (indexed range scan). Prepends older history and anchors the viewport so
  // the user's current read position doesn't jump.
  const loadOlder = useCallback(async () => {
    if (loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const vp = getViewport();
    const prevHeight = vp ? vp.scrollHeight : 0;
    const prevTop = vp ? vp.scrollTop : 0;
    try {
      const before = messages[0]?.created_at;
      const res = await api.get('/community/messages', { params: { league_id: leagueId || 'global', limit: 50, before } });
      const older = res.data.messages || [];
      isPrependRef.current = true;
      setMessages(prev => mergeAsc(prev, older));
      setHasMore(!!res.data.has_more);
      requestAnimationFrame(() => {
        const v = getViewport();
        if (v) v.scrollTop = prevTop + (v.scrollHeight - prevHeight);
      });
    } catch (e) { /* non-fatal */ }
    finally { setLoadingOlder(false); }
  }, [leagueId, messages, loadingOlder, getViewport]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Web⇄mobile parity (Gemini audit 2026-06, P3): poll every 10s so an open
  // chat shows new messages without a manual refresh — mirrors the mobile
  // ChatScreen 10s interval. `loading` stays false after the first load, so
  // polls refresh silently (no skeleton flicker).
  useEffect(() => {
    const iv = setInterval(loadMessages, 10_000);
    return () => clearInterval(iv);
  }, [loadMessages]);

  // v5.7.91 — Mark the chat as read on mount and on league change so the
  // Stage tab's unread badge resets the moment the user opens the room.
  // 2026-06 audit (P1): the effect previously depended on `messages.length`,
  // so EVERY 10s poll that added a message fired another POST /mark-read — a
  // steady write stream per open tab (thundering-herd risk at scale). We now
  // fire on mount/leagueId change + on window/tab refocus (so the badge still
  // resets when the user returns to an already-open chat) instead.
  useEffect(() => {
    const lid = leagueId || 'global';
    let cancelled = false;
    const markRead = async () => {
      try {
        await api.post(`/community/${lid}/mark-read`);
        if (!cancelled) {
          window.dispatchEvent(new CustomEvent('chat:read', { detail: { leagueId: lid } }));
        }
      } catch (e) { /* non-fatal — badge will catch up on next poll */ }
    };
    markRead();
    const onFocus = () => { if (document.visibilityState === 'visible') markRead(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [leagueId]);

  useEffect(() => {
    // Auto-scroll to bottom — but NOT when we just prepended older history,
    // and only follow new messages if the user is already near the bottom
    // (so reading back-history isn't yanked down by the 10s poll).
    if (isPrependRef.current) { isPrependRef.current = false; return; }
    const vp = getViewport();
    if (!vp) return;
    const nearBottom = vp.scrollHeight - vp.scrollTop - vp.clientHeight < 120;
    if (!hasScrolledOnceRef.current || nearBottom) {
      vp.scrollTop = vp.scrollHeight;
      if (messages.length > 0) hasScrolledOnceRef.current = true;
    }
  }, [messages, getViewport]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await api.post('/community/messages', {
        league_id: leagueId || 'global',
        content: newMessage.trim()
      });
      setNewMessage('');
      await loadMessages();
    } catch (e) {
      const status = e.response?.status;
      // 403 = moderation mute/ban — surface a clear, persistent banner
      // (parity with mobile ChatScreen) instead of silently mocking.
      if (status === 403) {
        toast.error(e.response?.data?.detail || 'Message blocked by moderation.');
        refreshMuted();
      } else {
        // 2026-06 audit (P1, 3/3 consensus): do NOT fabricate a successful
        // send on a non-403 error. Previously we injected a fake-id local echo
        // which (a) duplicated against the real message on the next 10s poll
        // and (b) falsely showed a rate-limited/failed message as "sent".
        // Surface the error and KEEP the draft so the user can retry.
        toast.error(
          e.response?.data?.detail || 'Couldn’t send your message — please try again.'
        );
      }
    } finally { setSending(false); }
  };

  // Caller's own moderation status → persistent "you're muted" banner.
  const refreshMuted = useCallback(async () => {
    try {
      const res = await api.get('/community/moderation/status');
      setMuted(res.data?.is_muted ? res.data : null);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { refreshMuted(); }, [refreshMuted]);

  const reportMessage = async (messageId, reason) => {
    try {
      const res = await api.post(`/community/messages/${messageId}/report`, { reason });
      const ref = res.data?.reference;
      toast.success(`Report received${ref ? ` · ref ${ref}` : ''}. Our team will review it within 24h.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit report.');
    }
  };

  const blockAuthor = async (msg) => {
    try {
      await api.post('/community/block', { target_user_id: msg.user_id });
      setMessages((prev) => prev.filter((m) => m.user_id !== msg.user_id));
      toast.success(`Blocked ${msg.username || 'user'}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not block user.');
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="community-chat">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          {onBack && <BackButton onClick={onBack} label="" />}
          <MessageCircle className="h-5 w-5 text-primary" />
          <h3 className="text-sm font-semibold">{leagueName || 'Global Chat'}</h3>
        </div>
        <Badge variant="secondary" className="text-[10px]">
          <Users className="h-3 w-3 mr-1" /> {messages.length} messages
        </Badge>
      </div>

      {muted && (
        <div className="px-4 py-2 text-[11px] font-semibold bg-amber-500/10 text-amber-600 border-b border-amber-500/30" data-testid="chat-muted-banner" role="alert">
          You're muted from chat{muted.muted_until ? ` until ${new Date(muted.muted_until).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}.
          {muted.mute_reason ? ` (${muted.mute_reason})` : ''}
        </div>
      )}

      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-3" role="log" aria-live="polite" aria-relevant="additions">
          {hasMore && !loading && (
            <div className="flex justify-center pb-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={loadOlder}
                disabled={loadingOlder}
                className="text-xs text-muted-foreground rounded-full"
                data-testid="chat-load-older"
              >
                {loadingOlder ? 'Loading…' : 'Load older messages'}
              </Button>
            </div>
          )}
          {loading ? (
            [1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No messages yet. Start the conversation!
            </div>
          ) : (
            messages.map((msg) => {
              if (msg.is_system) {
                return (
                  <div key={msg.id} className="flex justify-center" data-testid={`chat-system-${msg.id}`}>
                    <div className="max-w-[85%] text-center text-xs text-foreground/80 bg-secondary/60 border border-border/40 rounded-full px-4 py-1.5">
                      {msg.content}
                    </div>
                  </div>
                );
              }
              const isMe = msg.user_id === user?.id;
              return (
                <div key={msg.id} className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${msg.id}`}>
                  {!isMe && (
                    <img
                      src={buildChatAvatar(msg)}
                      alt=""
                      className="h-7 w-7 rounded-full bg-secondary shrink-0 object-cover"
                      data-testid={`chat-avatar-${msg.id}`}
                    />
                  )}
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    isMe ? 'bg-primary text-primary-foreground' : 'bg-card border border-border/60'
                  }`}>
                    {!isMe && (
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-[10px] font-semibold opacity-70">{msg.username}</p>
                        {user && msg.user_id && (
                          <div className="ml-auto -mr-1">
                            <ReportBlockMenu
                              targetUserId={msg.user_id}
                              targetUsername={msg.username || 'this user'}
                              contentLabel="message"
                              onReport={(reason) => reportMessage(msg.id, reason)}
                              onBlock={() => blockAuthor(msg)}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-sm">{msg.content}</p>
                    <p className={`text-[9px] mt-1 ${isMe ? 'opacity-60' : 'text-muted-foreground'}`}>
                      {msg.created_at ? new Date(msg.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      <div className="p-3 border-t border-border/60">
        <div className="flex gap-2">
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            className="flex-1"
            maxLength={500}
            data-testid="chat-input"
          />
          <Button
            size="icon"
            onClick={sendMessage}
            disabled={sending || !newMessage.trim()}
            data-testid="chat-send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
